import React from "react";
import { Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { ImageField, StringArrayField, getItemLabel } from "./FieldHelpers";

// Render a single field value editor. Recursive via SectionEditor for objectArray.
function Field({ field, value, onChange, uploadImage }) {
  const type = field.type || "text";

  if (type === "textarea") {
    return (
      <textarea
        className="admin-input admin-textarea"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
      />
    );
  }

  if (type === "boolean") {
    return (
      <label className="admin-checkbox-wrap">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>Yes</span>
      </label>
    );
  }

  if (type === "color") {
    return (
      <div className="admin-color-wrap">
        <input
          type="color"
          value={value || "#1b4332"}
          onChange={(e) => onChange(e.target.value)}
          className="admin-color-picker"
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="admin-input"
          placeholder="#hex"
          style={{ flex: 1 }}
        />
      </div>
    );
  }

  if (type === "image") {
    return <ImageField value={value} onChange={onChange} uploadImage={uploadImage} />;
  }

  if (type === "stringArray") {
    return <StringArrayField value={value} onChange={onChange} />;
  }

  if (type === "objectArray") {
    // objectArray handling is done in ObjectEditor directly to avoid mutual recursion
    return <div className="admin-error">Nested arrays must be defined at top level.</div>;
  }

  return (
    <input
      type="text"
      className="admin-input"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function ObjectEditor({ value, onChange, fields, uploadImage }) {
  const v = value || {};
  function setField(key, val) {
    const next = Object.assign({}, v);
    next[key] = val;
    onChange(next);
  }

  return (
    <div className="admin-form">
      {fields.map((f) => (
        <div key={f.key} className="admin-form-row">
          <label className="admin-label">{f.label}</label>
          <Field
            field={f}
            value={v[f.key]}
            onChange={(val) => setField(f.key, val)}
            uploadImage={uploadImage}
          />
        </div>
      ))}
    </div>
  );
}

function ArrayEditor({ value, onChange, itemFields, uploadImage, compact }) {
  const arr = Array.isArray(value) ? value : [];

  function setAt(i, item) {
    const next = arr.slice();
    next[i] = item;
    onChange(next);
  }
  function removeAt(i) {
    if (!window.confirm("Remove this item?")) return;
    onChange(arr.filter((_, idx) => idx !== i));
  }
  function add() {
    const blank = {};
    itemFields.forEach((f) => {
      blank[f.key] = f.type === "boolean" ? false : "";
    });
    onChange(arr.concat([blank]));
  }
  function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    const next = arr.slice();
    const tmp = next[i];
    next[i] = next[j];
    next[j] = tmp;
    onChange(next);
  }

  const cardClass = compact ? "admin-array-item compact" : "admin-array-item";

  return (
    <div className="admin-array">
      {arr.map((item, i) => {
        const itemLabel = getItemLabel(item);
        return (
          <div key={i} className={cardClass}>
            <div className="admin-array-item-head">
              <div className="admin-array-item-idx">
                <span>{`#${i + 1}`}</span>
                {itemLabel ? <span className="admin-array-item-title"> {`— ${itemLabel}`}</span> : null}
              </div>
              <div className="admin-array-item-actions">
                <button type="button" className="admin-icon-btn" onClick={() => move(i, -1)} title="Up"><ArrowUp size={13} /></button>
                <button type="button" className="admin-icon-btn" onClick={() => move(i, 1)} title="Down"><ArrowDown size={13} /></button>
                <button type="button" className="admin-icon-btn danger" onClick={() => removeAt(i)} title="Remove"><Trash2 size={13} /></button>
              </div>
            </div>
            <ObjectEditor
              value={item}
              onChange={(updated) => setAt(i, updated)}
              fields={itemFields}
              uploadImage={uploadImage}
            />
          </div>
        );
      })}
      <button type="button" className="admin-btn-primary admin-btn-add" onClick={add}>
        <Plus size={14} /> Add new item
      </button>
    </div>
  );
}

function SectionEditor({ section, value, onChange, uploadImage }) {
  if (!section) return null;
  if (value == null) return null;

  if (section.type === "object") {
    return (
      <ObjectEditor
        value={value}
        onChange={onChange}
        fields={section.fields}
        uploadImage={uploadImage}
      />
    );
  }

  if (section.type === "array") {
    return (
      <ArrayEditor
        value={value}
        onChange={onChange}
        itemFields={section.itemFields}
        uploadImage={uploadImage}
      />
    );
  }

  if (section.type === "stringArray") {
    return <StringArrayField value={value} onChange={onChange} />;
  }

  return null;
}

export default SectionEditor;
